/**
 * Session network plumbing for SessionProvider.
 *
 * Pure async helpers (no React): fetchWithTimeout wraps fetch with an abort
 * timeout that also respects an external AbortSignal, and
 * refreshAccessTokenWithStatus performs the native-vs-web /auth/refresh call.
 * Both are imported back into SessionProvider and used by its profile/container/
 * invite calls. AUTH_BASE_URL is read from env exactly as before.
 */
import { env } from '@/lib/env'
import { Capacitor } from '@capacitor/core'
import { getAuthItem, setAuthItem } from '@/lib/authStorage'

export const AUTH_BASE_URL = env.authBaseUrl

/**
 * Fetch with timeout to prevent hanging requests
 * If an external AbortSignal is passed in init, both signals are respected
 */
export async function fetchWithTimeout(
  input: RequestInfo,
  init?: RequestInit,
  timeoutMs = 10000,
): Promise<Response> {
  const timeoutController = new AbortController()
  const externalSignal = init?.signal

  const id = setTimeout(() => {
    timeoutController.abort()
  }, timeoutMs)

  // If external signal exists, listen to it and abort timeout controller
  if (externalSignal) {
    if (externalSignal.aborted) {
      timeoutController.abort()
    } else {
      externalSignal.addEventListener('abort', () => {
        timeoutController.abort()
      })
    }
  }

  try {
    const response = await fetch(input, {
      ...init,
      signal: timeoutController.signal,
    })
    return response
  } finally {
    clearTimeout(id)
  }
}

export async function refreshAccessTokenWithStatus(
  storedToken: string | null,
): Promise<{
  token: string | null
  email?: string | null
  status?: number
  error?: Error
}> {
  try {
    const isNative = Capacitor.isNativePlatform()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (storedToken) {
      headers['Authorization'] = `Bearer ${storedToken}`
    }
    // Native apps send refresh token via header (cookies unreliable across force-quit/upgrade)
    if (isNative) {
      headers['X-Client-Platform'] = 'capacitor'
      const storedRefresh = getAuthItem('xavior_refresh_token')
      if (storedRefresh) {
        headers['X-Refresh-Token'] = storedRefresh
      }
    }

    const response = await fetchWithTimeout(
      `${AUTH_BASE_URL}/auth/refresh`,
      {
        method: 'POST',
        headers,
        credentials: 'include', // Still send cookie as fallback
        cache: 'no-store',
      },
      10000,
    )

    if (!response.ok) {
      return { token: null, status: response.status }
    }

    const data = await response.json().catch(() => null)
    const token =
      typeof data?.accessToken === 'string' ? data.accessToken : null
    const email = typeof data?.email === 'string' ? data.email : null

    // Store rotated refresh token for native apps
    if (isNative && typeof data?.refreshToken === 'string') {
      setAuthItem('xavior_refresh_token', data.refreshToken)
    }

    return { token, email, status: response.status }
  } catch (error) {
    return { token: null, error: error as Error }
  }
}
